import { useMemo } from 'react';
import {
  PageSection,
  Stack,
  TextTitle,
  Icon,
  Spacer,
  DataTable,
} from '@pypestream/design-system';
import { ColumnDef } from '@tanstack/react-table';

import { DataFileType } from '../../utils';
import { Upload } from '../upload';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { useUploadPythonFiles } from '../../hooks';

export interface ActionFile {
  name: string;
  path: string;
  loading?: boolean;
}

export type RowSelectionChangeProps = ActionFile & {
  type: ActionSectionProps['type'];
};

export type ActionSectionProps = {
  title: string;
  label: string;
  columns: ColumnDef<ActionFile>[];
  data: ActionFile[];
  type: Omit<DataFileType, 'assetData'>;
  onRowSelection: (props: RowSelectionChangeProps) => void;
};

export const ActionSection = ({
  title,
  label,
  columns,
  data,
  type,
  onRowSelection,
}: ActionSectionProps) => {
  const { versionId } = useProStudioCtxSelector((ctx) => ({
    versionId:
      ctx.customerId && ctx.botName && ctx.draftVersion
        ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
        : undefined,
  }));

  const { uploadingFiles, handleUpload } = useUploadPythonFiles(type);

  const tableData = useMemo(() => {
    return [...data, ...uploadingFiles];
  }, [data, uploadingFiles]);

  return (
    <PageSection
      test-id={`ps-${type.toLowerCase()}-action-section`}
      size="small"
    >
      <Stack alignItems="center" justifyContent="space-between">
        <TextTitle
          test-id={`ps-${type.toLowerCase()}-action-title`}
          size="xsmall"
        >
          {title}
          <Icon
            test-id={`ps-${type.toLowerCase()}-action-title-icon`}
            name="info"
            color="gray"
            size="small"
            slot="suffix"
          />
        </TextTitle>
        <Upload
          test-id={`ps-${type.toLowerCase()}-action-upload`}
          disabled={!versionId}
          label={label}
          onUpload={handleUpload}
        />
      </Stack>
      <Spacer size="large" />
      <DataTable
        test-id={`ps-${type.toLowerCase()}-action-table`}
        size="small"
        columns={columns}
        data={tableData}
        onRowSelectionChange={(event) => {
          if (event.detail.selected && event.detail.selected.length) {
            onRowSelection({
              name: event.detail.selected[0].original.name,
              path: event.detail.selected[0].original.path,
              type,
            });
          }
        }}
        enableRowSelection
      />
    </PageSection>
  );
};
